import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IPermission } from 'common/constant/IPermission';
import { BehaviorSubject, Observable, shareReplay, take, tap } from 'rxjs';
import { CognitoService } from 'services/authentication/cognito/cognito.service';
import { EdgeDevice } from 'services/edge-device/edge-device.service';
import { Api } from 'services/http/api-endpoints';
import { HttpService } from 'services/http/http.service';
import { Endpoint } from 'services/http/interface/endpoint';
import { Route } from 'services/http/routes';
import { TokenService } from 'services/token/token.service';
import { Attributes } from './model/attributes';
import { User } from './model/user';
import { Create } from './request/create';
import { Update } from './request/update';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private edgeDevices: BehaviorSubject<Array<EdgeDevice>> = new BehaviorSubject<Array<EdgeDevice>>([])

  constructor(
    private httpService: HttpService,
    private cognitoService: CognitoService,
    private tokenService: TokenService,
  ) {
    this.refreshUsers().subscribe()
  }

  private usersRefreshed: BehaviorSubject<Array<User>> = new BehaviorSubject<Array<User>>([])

  public get UsersRefreshed(): BehaviorSubject<Array<User>> {
    return this.usersRefreshed
  }

  public refreshUsers(poolId?: string): Observable<Array<User>> {
    return this.getCachedUserList(poolId).pipe(
      take(1),
      tap(users => {
        this.usersRefreshed.next(users)
      })
    )
  }

  private getCachedUserList(poolId?: string): Observable<Array<User>> {
    return this.listUsers(poolId)
      .pipe(shareReplay(1))
  }

  public create(username: string, email: string, firstName: string, lastName: string, admin: boolean, locale: string, poolId?: string): Observable<any> {
    const body: Create = new Create(username, email, admin, new Attributes(firstName, lastName, locale))
    return this.httpService.request(Api.user, Route.User.Create, null, body, this.createPoolIdHeader(poolId))
  }

  private listUsers(poolId?: string): Observable<Array<User>> {
    return this.httpService.request(Api.users, Route.Users.List, null, null, this.createPoolIdHeader(poolId))
  }

  public update(id: string, firstName: string, lastName: string, admin: boolean, emailAddress: string, locale: string, poolId?: string): Observable<any> {
    const body: Update = new Update(admin, emailAddress, new Attributes(firstName, lastName, locale))
    const endpoint: Endpoint = Api.getEndpoint(Api.user, Route.User.Update);
    return this.httpService.request(Api.user, Route.User.Update, Api.createParameters(endpoint.route, [id]), body, this.createPoolIdHeader(poolId))
  }

  public toggleState(id: string, enable: boolean, poolId?: string): any {
    const state = enable ? 'enable' : 'disable'
    const endpoint: Endpoint = Api.getEndpoint(Api.user, Route.User.ToggleState);
    return this.httpService.request(Api.user, Route.User.ToggleState, Api.createParameters(endpoint.route, [id, state]), null, this.createPoolIdHeader(poolId))
  }

  public hasPermission(permissions: Array<IPermission>): boolean {
    let returnValue: boolean = false
    permissions = Array.isArray(permissions) ? permissions : [permissions]
    const types = [... new Set(permissions.map(permission => permission.claimType))]
    if (types.length > 1) {
      for (const type of types) {
        const claims = permissions.filter(permission => permission.claimType === type).map(permission => permission.identifier)
        returnValue = this.tokenService.anyClaims(claims, type)
        if (returnValue) {
          break
        }
      }
    } else {
      const claims = permissions.map(permission => permission.identifier)
      returnValue = this.tokenService.anyClaims(claims, types[0])
    }
    return returnValue
  }

  // public sources(id: string): Observable<any> {
  //   const action: string = Route.User.Sources.Fetch;
  //   const endpoint: Endpoint = Api.getEndpoint(Api.user, action);
  //   return this.httpService.request(Api.user, action, Api.createParameters(endpoint.route, [id]))
  // }

  // public sourcesSet(id: string, sources: Array<string>): Observable<any> {
  //   const action: string = Route.User.Sources.Set;
  //   const endpoint: Endpoint = Api.getEndpoint(Api.user, action);
  //   return this.httpService.request(Api.user, action, Api.createParameters(endpoint.route, [id]), sources)
  // }

  public getEdgeDevices(id: string): Observable<Array<EdgeDevice>> {
    const action: string = Route.User.EdgeDevices.Fetch;
    const endpoint: Endpoint = Api.getEndpoint(Api.user, action);
    return this.httpService.request(Api.user, action, Api.createParameters(endpoint.route, [id]))
  }

  public setEdgeDevices(id: string, edgeIds: Array<string>): Observable<Array<EdgeDevice>> {
    const action: string = Route.User.EdgeDevices.Set;
    const endpoint: Endpoint = Api.getEndpoint(Api.user, action);
    return this.httpService.request(Api.user, action, Api.createParameters(endpoint.route, [id]), edgeIds)
  }


  private createPoolIdHeader(poolId?: string): HttpHeaders | undefined {
    poolId = poolId ?? this.cognitoService.PoolId
    return !!poolId ? new HttpHeaders({ poolId: poolId }) : undefined
  }
}
